import React, { useState } from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BrokerageDupesSection({ onChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cleanup/brokerage-duplicates");
      setGroups(data.groups || []);
      if ((data.groups || []).length === 0) toast.info("No duplicate brokerages found");
      else toast.success(`Found ${data.groups.length} duplicate group(s)`);
    } catch (e) {
      toast.error("Scan failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (group, keepId) => {
    setMergeBusy(true);
    const removeIds = group.brokerages.filter((b) => b.id !== keepId).map((b) => b.id);
    try {
      const { data } = await api.post("/cleanup/brokerage-duplicates/merge", {
        keep_id: keepId,
        remove_ids: removeIds,
        canonical_name: group.suggested_name || group.brokerages.find((b) => b.id === keepId)?.name || "",
      });
      toast.success(`Merged! ${data.realtors_reassigned} realtors reassigned, ${data.brokerages_removed} removed`);
      setGroups((prev) => prev.filter((g) => g !== group));
      if (onChange) onChange();
    } catch (e) {
      toast.error("Merge failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="brokerage-dupes-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-slate-600">
          Finds brokerages with the same canonical name <strong>and</strong> the same address.
          Legitimately different branches (e.g. two RE/MAX offices at different locations) are <em>not</em> flagged.
        </p>
        <Button onClick={scan} disabled={loading} variant="outline" className="shrink-0 min-h-[44px]" data-testid="brokerage-dupes-scan">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Scan for duplicates
        </Button>
      </div>

      {groups.length === 0 && !loading ? (
        <p className="text-sm text-slate-500 italic">Run a scan to check for duplicate brokerages.</p>
      ) : null}

      {groups.map((g, idx) => (
        <div
          key={idx}
          className="rounded-md border border-slate-200 bg-white overflow-hidden"
          data-testid={`brokerage-dupe-group-${idx}`}
        >
          <div className="p-3 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <span className="font-medium text-slate-900">
                  {g.match_type === "name_and_address" ? "Name + Address match" : "Name match (no address)"}
                </span>
                <span className="text-xs text-slate-500 ml-2">
                  Canonical: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{g.canon_name}</code>
                </span>
              </div>
              <span className="text-xs text-slate-600">
                {g.total_realtors} total realtors · {g.brokerages.length} records
              </span>
            </div>
            {g.suggested_name ? (
              <div className="text-xs text-emerald-700 mt-1">
                Suggested canonical name: <strong>{g.suggested_name}</strong>
              </div>
            ) : null}
          </div>

          <div className="divide-y divide-slate-100">
            {g.brokerages.map((b) => (
              <div key={b.id} className="flex items-center gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-900">{b.name}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {b.office_address || "No address"} · {b.realtor_count} realtors
                    {b.interaction_log_count > 0 ? ` · ${b.interaction_log_count} interactions` : ""}
                    {b.notes ? " · has notes" : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 text-xs min-h-[36px]"
                  onClick={() => handleMerge(g, b.id)}
                  disabled={mergeBusy}
                  data-testid={`brokerage-keep-${b.id}`}
                >
                  {mergeBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Keep this one
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
